import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { InterpolationService, parseCSV, TableRow, TimeSeries, UTCDate } from 'map-wald';
import { forkJoin, map, Observable, of, shareReplay } from 'rxjs';
import { switchAll, switchMap, tap } from 'rxjs/operators'
import { DataMode, FlattenedLayerDescriptor, getAggregation, TimePeriod } from './data';
import { DataModeUtils, TimesteppedDataOperator } from './utils';
import { TimePeriodUtils } from './utils/timeperiod-utils';
import { environment } from '../environments/environment';
import { ConfigService } from './config.service';

const DATE_COLUMNS=['','time','time ', 'Unnamed: 0'];

@Injectable({
  providedIn: 'root'
})
export class ZonalService {
  parsedResultsCache: {[key:string]:Observable<DatedTableRow[]>} = {}
  constructor(private http:HttpClient, private config:ConfigService) { }

  getRaw(dataLayer:FlattenedLayerDescriptor,featureLayer:string):Observable<DatedTableRow[]>{
    const url = environment.zonalCSVs + InterpolationService.interpolate(dataLayer.zonal,Object.assign({},dataLayer,{vectorLayer:featureLayer}));

    if(!this.parsedResultsCache[url]){
      const urlWithTime = url + '?t=' + new Date().getTime();
      this.parsedResultsCache[url] = this.http.get(urlWithTime,{
        responseType:'text'
      }).pipe(
        map(txt=>{
          const data = parseCSV(txt,{
            headerRows:1
          });
          return data as DatedTableRow[];
        }),
        tap(data=>{
          data.forEach(row=>{
            if(!row['date']){
              for(let dc of DATE_COLUMNS){
                if(row[dc]){
                  row['date'] = row[dc];
                  delete row[dc];
                  break;
                }
              }
            }
          });
        }),
        tap(data=>{
          data.forEach(row=>{
            let date = row['date'];
            if((typeof(date)==='number')||(typeof(date)==='string')){
              row['date'] = new Date(date);
            }
          });
        }),
        map(data=>{
          if(dataLayer.timePeriod){
            return data.filter(row=>dataLayer.timePeriod.contains(row.date));
          }
          return data;
        }),
        shareReplay());
    }
    return this.parsedResultsCache[url];
  }

  getTimeSeries(dataLayer:FlattenedLayerDescriptor,featureLayer:string,featureID:string):Observable<TimeSeries> {
    return this.getRaw(dataLayer,featureLayer).pipe(
      map(data=>{
        let result:TimeSeries;
        result = {
          dates: data.map(row=>row.date),
          values: data.map(row=>row[featureID])
        };
        return result;
      }));
  }

  getGlobalTimeSeries(dataLayer:FlattenedLayerDescriptor):Observable<TimeSeries> {
    return this.config.vectorLayers$.pipe(
      map(layers=>layers.find(l=>l.attributes)),
      map(vectorLayer=>{
        return forkJoin([
          this.getRaw(dataLayer,vectorLayer.zonal),
          this.getAttributes(vectorLayer.attributes),
          of(vectorLayer)
        ]);
      }),
      switchAll(),
      map(([zonals,attributes,vectorLayer])=>{
        const result:TimeSeries = {
          dates: zonals.map(row=>row.date),
          values: zonals.map(row=>dataLayer.globalAggregation==='sum'?sumRow(row):weightedAverage(row,attributes,vectorLayer.keyField||vectorLayer.tileLayers?.[0].keyField))
        };
        return result;
      }));
  }

  getAttributes(attributesURL: string): Observable<TableRow[]> {
    const url = `${environment.geojsons}${attributesURL}`;
    return this.http.get(url,{
      responseType:'text'
    }).pipe(
      map(txt=>parseCSV(txt)));
  }

  getValues(layer:FlattenedLayerDescriptor, featureLayer:string, dataMode: DataMode, timeperiod: TimePeriod): Observable<{[key:string]:number}>{
    return this.getRaw(layer,featureLayer).pipe(
      map((table:DatedTableRow[])=>{
        return table.map(r=>tableFunc([r],nums=>nums[0]===-9999?NaN:nums[0]));
      }),
      map((table:DatedTableRow[])=>{
        return DataModeUtils.processData(dataMode,timeperiod,getAggregation(layer),table,TABLE_OPERATOR);
      }));
  }
}

function tableFunc(rows: DatedTableRow[], fn: (nums: number[]) => number): DatedTableRow {
  const result:DatedTableRow = {
    date:null
  };

  Object.keys(rows[0]).forEach(k=>{
    if((k==='')||(k==='date')){
      result[k]=rows[0][k];
    } else {
      result[k] = fn(rows.map(r=>r[k]));
    }
  });
  return result;
}

function validVal(val: number): boolean {
  return !isNaN(val)&&isFinite(val)&&(val!==null)&&(val!==undefined);
}

function sumValid(vals:number[]): number {
  return vals.filter(validVal).reduce((lhs,rhs)=>lhs+rhs,0.0);
}

function numValid(vals:number[]): number {
  return vals.filter(validVal).length;
}

function sumRows(rows: DatedTableRow[]): DatedTableRow {
  return tableFunc(rows,sumValid);
}

function validCount(rows: DatedTableRow[]): DatedTableRow {
  return tableFunc(rows,numValid);
}

function scaleRow(row: DatedTableRow, scale: number): DatedTableRow {
  return tableFunc([row],nums=>nums[0]*scale);
}

function averageDoF(rows: DatedTableRow[],dof?:number): DatedTableRow {
  dof = dof || 0.0;
  let counts = validCount(rows);
  if(dof){
    counts = addConst(counts,-dof);
  }
  return divideRows(sumRows(rows),counts);
}

function averageRows(rows: DatedTableRow[]): DatedTableRow {
  const counts = validCount(rows);
  return divideRows(sumRows(rows),counts);
}

function subtract(lhs: DatedTableRow, rhs: DatedTableRow): DatedTableRow {
  return tableFunc([lhs,rhs],(nums:number[])=>nums[0]-nums[1]);
}

function addConst(row: DatedTableRow, constant: number): DatedTableRow {
  return tableFunc([row],nums=>nums[0]+constant);
}

function divideRows(lhs: DatedTableRow, rhs: DatedTableRow): DatedTableRow {
  return tableFunc([lhs,rhs],(nums:number[])=>nums[0]/nums[1]);
}

function stdDeviation(rows: DatedTableRow[]): DatedTableRow {
  const mean = averageRows(rows);
  const deviations = rows.map(row=>subtract(row,mean));
  const squaredDeviations = deviations.map(row=>tableFunc([row],nums=>nums[0]*nums[0]));
  const variance = averageDoF(squaredDeviations,1);
  const std = tableFunc([variance],nums=>Math.sqrt(nums[0]));
  return std;
}

function minRows(rows: DatedTableRow[]): DatedTableRow {
  return tableFunc(rows,nums=>Math.min(...nums));
}

function maxRows(rows: DatedTableRow[]): DatedTableRow {
  return tableFunc(rows,nums=>Math.max(...nums));
}

function trendRows(rows: DatedTableRow[]): DatedTableRow {
  const xs = rows.map((r,i)=>r['date'].getUTCFullYear());
  const meanX = sumValid(xs)/numValid(xs);
  const xvar = sumValid(xs.map(v => (v - meanX) * (v - meanX))); // ssxm
  const sumX2 = sumValid(xs.map(v => v * v)); // ssx2

  return tableFunc(rows,ys=>{
    const P_VALUE_THRESHOLD = 0.05;
    const meanY = sumValid(ys)/numValid(ys);
    const xyCov = sumValid(xs.map((v, i) => (v - meanX) * (ys[i] - meanY))); // ssxym
    const yvar = sumValid(ys.map(v => (v - meanY) * (v - meanY))); // ssym
    const slope = xyCov / xvar;
    const intercept = meanY - slope * meanX;
    const relativeSlope = slope / meanY;

    const n = xs.length;
    let sumSquaredError = 0;
    for (let i = 0; i < n; i++) {
      const predictedY = slope * xs[i] + intercept;
      sumSquaredError += Math.pow(ys[i] - predictedY, 2);
    }

    let r = xyCov / Math.sqrt(xvar * yvar);
    if(r>1){
      r = 1;
    }
    if(r<-1){
      r = -1;
    }

    const stdError = Math.sqrt(sumSquaredError / (n - 2));
    const stdErrorSlope = stdError / Math.sqrt(sumX2 - n * Math.pow(meanX, 2));
    const tStatistic = r * Math.sqrt((n - 2) / (1 - r * r));

    let pValue = studentTSF(tStatistic,n-2);
    if(pValue>0.5){
      pValue = 2*(1-pValue);
    } else {
      pValue = 2*pValue;
    }
    if(pValue>P_VALUE_THRESHOLD){
      return NaN;
    }
    return slope;
  });
}

interface DatedTableRow extends TableRow {
  date: UTCDate;
}
const TABLE_OPERATOR: TimesteppedDataOperator<DatedTableRow> = {
  recurringTimePeriod: null,
  sum: sumRows,
  subtract: subtract,
  average: averageRows,
  scale: scaleRow,
  divide: divideRows,
  stddev: stdDeviation,
  min: minRows,
  max: maxRows,
  trend: trendRows
};

function weightedAverage(row: DatedTableRow, attributes: TableRow[],keyColumn:string): any {
  let total = 0.0;
  let totalWeight = 0.0;
  attributes.forEach(attrRow=>{
    const key = attrRow[keyColumn];
    const val = row[key];
    if((val===null)||(val===undefined)||(val===-9999)){
      return;
    }
    const weight = attrRow.area;
    total += val*weight;
    totalWeight += weight;
  });
  return total/totalWeight;
}

function sumRow(row: DatedTableRow): number {
  let total = 0.0;
  Object.keys(row).forEach(k=>{
    if((k==='')||(k==='date')){
      return;
    }
    const val = row[k];
    if((val===null)||(val===undefined)||(val===-9999)){
      return;
    }
    total += val;
  });
  return total;
}

function studentTSF(X:number, df:number) {
  let tcdf:number;
  if (df<=0) {
    alert("Degrees of freedom must be positive")
  } else {
    const A=df/2;
    const S=A+.5;
    const Z=df/(df+X*X);
    const BT=Math.exp(logGamma(S)-logGamma(.5)-logGamma(A)+A*Math.log(Z)+.5*Math.log(1-Z));
    let betacdf:number;
    if (Z<(A+1)/(S+2)) {
      betacdf=BT*betinc(Z,A,.5)
    } else {
      betacdf=1-BT*betinc(1-Z,.5,A)
    }
    if (X<0) {
      tcdf=betacdf/2
    } else {
      tcdf=1-betacdf/2
    }
  }
  tcdf=Math.round(tcdf*100000)/100000;
  return tcdf;
}

function logGamma(Z:number) {
  let S=1+76.18009173/Z-86.50532033/(Z+1)+24.01409822/(Z+2)-1.231739516/(Z+3)+.00120858003/(Z+4)-.00000536382/(Z+5);
  let LG= (Z-.5)*Math.log(Z+4.5)-(Z+4.5)+Math.log(S*2.50662827465);
	return LG
}

function betinc(X:number,A:number,B:number) {
	let A0=0;
	let B0=1;
	let A1=1;
	let B1=1;
	let M9=0;
	let A2=0;
	let C9;
	while (Math.abs((A1-A2)/A1)>.00001) {
		A2=A1;
		C9=-(A+M9)*(A+B+M9)*X/(A+2*M9)/(A+2*M9+1);
		A0=A1+C9*A0;
		B0=B1+C9*B0;
		M9=M9+1;
		C9=M9*(B-M9)*X/(A+2*M9-1)/(A+2*M9);
		A1=A0+C9*A1;
		B1=B0+C9*B1;
		A0=A0/B1;
		B0=B0/B1;
		A1=A1/B1;
		B1=1;
	}
	return A1/A
}
